import type { AuditQuery } from '@/features/audit/api';

/** The Audit log filter form as typed (dates are clinic dates, YYYY-MM-DD). */
export interface AuditFilterForm {
  area: string;
  fromDate: string;
  toDate: string;
  person: string;
  recordType: string;
  recordId: string;
  changesOnly: boolean;
}

export const EMPTY_FILTERS: AuditFilterForm = {
  area: '',
  fromDate: '',
  toDate: '',
  person: '',
  recordType: '',
  recordId: '',
  changesOnly: false,
};

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** The day after a clinic date (YYYY-MM-DD). */
function nextDay(ymd: string) {
  const d = new Date(`${ymd}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + 1);
  return d.toISOString().slice(0, 10);
}

/** Field errors, in the API's limits (RL-1816 AuditQueryDto). */
export function filterErrors(f: AuditFilterForm) {
  const person = f.person.trim();
  const type = f.recordType.trim();
  const id = f.recordId.trim();
  return {
    person:
      person && (person.length < 2 || person.length > 80)
        ? 'Type 2 to 80 letters of the name.'
        : undefined,
    recordType:
      type && !/^[a-z_]{2,64}$/.test(type)
        ? 'Lower-case letters and _ only, e.g. patient or bill.'
        : undefined,
    recordId: id && !UUID.test(id) ? 'Paste the full record ID.' : undefined,
    toDate:
      f.fromDate && f.toDate && f.toDate < f.fromDate
        ? 'Must be on or after the from date.'
        : undefined,
  };
}

/**
 * GET /audit-events filters: clinic dates become instants in Malaysia time,
 * `to` is the start of the day after (the API's `to` is exclusive).
 */
export function toAuditQuery(f: AuditFilterForm, page: number): AuditQuery {
  return {
    area: f.area || undefined,
    from: f.fromDate ? `${f.fromDate}T00:00:00+08:00` : undefined,
    to: f.toDate ? `${nextDay(f.toDate)}T00:00:00+08:00` : undefined,
    actorName: f.person.trim() || undefined,
    entityType: f.recordType.trim() || undefined,
    entityId: f.recordId.trim() || undefined,
    changesOnly: f.changesOnly || undefined,
    page,
  };
}
